import React, { useEffect, useState, useCallback } from 'react';
import { dailyTaskAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORY = {
  work:    { label: 'Công việc',    color: '#2563eb', bg: '#dbeafe',  icon: '⚙️' },
  admin:   { label: 'Hành chính',   color: '#7c3aed', bg: '#ede9fe',  icon: '📂' },
  report:  { label: 'Báo cáo',      color: '#d97706', bg: '#fef3c7',  icon: '📊' },
  meeting: { label: 'Họp',          color: '#0891b2', bg: '#cffafe',  icon: '🤝' },
  other:   { label: 'Khác',         color: '#64748b', bg: '#f1f5f9',  icon: '📌' },
};

const STATUS_CFG = {
  pending: { label: 'Chưa làm', color: '#64748b', bg: '#f1f5f9' },
  done:    { label: 'Hoàn thành', color: '#16a34a', bg: '#dcfce7' },
  skipped: { label: 'Bỏ qua',    color: '#9ca3af', bg: '#f3f4f6' },
};

function todayStr() { return new Date().toISOString().slice(0, 10); }
function fmtDateVN(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function EmployeeTasksPage() {
  const { user } = useAuth();
  const canManage = ['admin', 'hr_manager'].includes(user?.role);
  const isManagerView = ['admin', 'hr_manager', 'manager', 'hr_staff'].includes(user?.role);

  const [tab, setTab] = useState('overview'); // 'overview' | 'detail' | 'manage'
  const [date, setDate] = useState(todayStr());
  const [summary, setSummary] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [dayLogs, setDayLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [filterEmpDef, setFilterEmpDef] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load overview summary
  const loadSummary = useCallback(async () => {
    if (!isManagerView) return;
    try {
      const res = await dailyTaskAPI.summary(date);
      setSummary(res.data.data);
    } catch (e) { setError(e.response?.data?.message || 'Lỗi tải dữ liệu'); }
  }, [date, isManagerView]);

  // Load day logs for selected employee
  const loadDayLogs = useCallback(async (empId) => {
    setLoading(true);
    try {
      const params = { date };
      if (empId) params.employee_id = empId;
      const res = await dailyTaskAPI.logs(params);
      setDayLogs(res.data.data);
    } catch (e) { setError(e.response?.data?.message || 'Lỗi tải nhiệm vụ'); }
    finally { setLoading(false); }
  }, [date]);

  // Load task definitions for manage tab
  const loadDefinitions = useCallback(async () => {
    try {
      const params = {};
      if (filterEmpDef) params.employee_id = filterEmpDef;
      const res = await dailyTaskAPI.listDefinitions(params);
      setDefinitions(res.data.data);
    } catch (e) { setError(e.response?.data?.message || 'Lỗi tải danh sách'); }
  }, [filterEmpDef]);

  useEffect(() => {
    employeeAPI.list({ limit: 200 }).then(r => setEmployees(r.data.data || []));
  }, []);

  useEffect(() => {
    if (tab === 'overview' && isManagerView) loadSummary();
    if (tab === 'detail') loadDayLogs(selectedEmpId);
    if (tab === 'manage') loadDefinitions();
  }, [tab, date, selectedEmpId, loadSummary, loadDayLogs, loadDefinitions]);

  // Employee opens their own tasks
  useEffect(() => {
    if (!isManagerView) {
      setTab('detail');
      setSelectedEmpId(null);
      loadDayLogs(null);
    }
  }, [isManagerView, loadDayLogs]);

  const handleClickEmployee = (empId) => {
    setSelectedEmpId(empId);
    setTab('detail');
  };

  const handleUpdateLog = async (logId, status, note) => {
    try {
      await dailyTaskAPI.updateLog(logId, { status, note });
      setDayLogs(logs => logs.map(l => l.log_id === logId ? { ...l, status, note, completed_at: status === 'done' ? new Date().toISOString() : null } : l));
      if (tab === 'detail' && isManagerView) setTimeout(loadSummary, 300);
    } catch (e) { setError(e.response?.data?.message || 'Lỗi cập nhật'); }
  };

  // Stats for overview
  const totalEmp = summary.length;
  const fullyDone = summary.filter(s => s.total_tasks > 0 && s.done_tasks === s.total_tasks).length;
  const avgPct = summary.length > 0
    ? Math.round(summary.reduce((a, s) => a + (s.total_tasks > 0 ? s.done_tasks / s.total_tasks : 0), 0) / summary.length * 100)
    : 0;
  const overallDone = summary.reduce((a, s) => a + Number(s.done_tasks), 0);
  const overallTotal = summary.reduce((a, s) => a + Number(s.total_tasks), 0);

  const inputStyle = { padding: '7px 12px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13 };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Nhiệm vụ hàng ngày</h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>Theo dõi danh sách công việc cố định mỗi ngày của từng nhân viên</p>
      </div>

      {/* Date picker + tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Ngày:</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          <button onClick={() => setDate(todayStr())} style={{ ...inputStyle, background: date === todayStr() ? '#6366f1' : '#fff', color: date === todayStr() ? '#fff' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
            Hôm nay
          </button>
        </div>

        {isManagerView && (
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
            {[['overview','📊 Tổng quan'],['detail','📋 Chi tiết'],...(canManage ? [['manage','⚙️ Quản lý']] : [])].map(([v, lbl]) => (
              <button key={v} onClick={() => setTab(v)} style={{
                padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: tab === v ? '#fff' : 'transparent',
                color: tab === v ? '#1e293b' : '#64748b',
                boxShadow: tab === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{lbl}</button>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <OverviewTab
          summary={summary} date={date}
          totalEmp={totalEmp} fullyDone={fullyDone} avgPct={avgPct}
          overallDone={overallDone} overallTotal={overallTotal}
          onClickEmployee={handleClickEmployee}
        />
      )}

      {/* ── DETAIL TAB ── */}
      {tab === 'detail' && (
        <DetailTab
          date={date} dayLogs={dayLogs} loading={loading}
          employees={employees} selectedEmpId={selectedEmpId}
          isManagerView={isManagerView}
          onSelectEmployee={id => { setSelectedEmpId(id); loadDayLogs(id); }}
          onUpdateLog={handleUpdateLog}
        />
      )}

      {/* ── MANAGE TAB ── */}
      {tab === 'manage' && canManage && (
        <ManageTab
          definitions={definitions} employees={employees}
          filterEmpDef={filterEmpDef} setFilterEmpDef={setFilterEmpDef}
          onRefresh={loadDefinitions}
        />
      )}
    </div>
  );
}

/* ══════════════════ OVERVIEW ══════════════════ */
function OverviewTab({ summary, date, totalEmp, fullyDone, avgPct, overallDone, overallTotal, onClickEmployee }) {
  return (
    <div>
      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          ['Nhân viên có nhiệm vụ', totalEmp, '#6366f1'],
          ['Hoàn thành 100%', fullyDone, '#16a34a'],
          ['Tỉ lệ trung bình', avgPct + '%', '#0891b2'],
          [`Tổng (${overallDone}/${overallTotal})`, overallTotal > 0 ? Math.round(overallDone/overallTotal*100) + '%' : '—', '#d97706'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background: '#fff', borderRadius: 12, padding: '16px 22px', flex: 1, minWidth: 130, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${color}` }}>
            <div style={{ fontSize: 26, fontWeight: 700, color }}>{val}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12, fontWeight: 600 }}>
        📅 {fmtDateVN(date)}
      </div>

      {summary.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 12 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div>Chưa có dữ liệu nhiệm vụ cho ngày này</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {summary.map(s => <EmployeeSummaryCard key={s.employee_id} s={s} onClick={() => onClickEmployee(s.employee_id)} />)}
        </div>
      )}
    </div>
  );
}

function EmployeeSummaryCard({ s, onClick }) {
  const pct = s.total_tasks > 0 ? Math.round((s.done_tasks / s.total_tasks) * 100) : 0;
  const color = pct === 100 ? '#16a34a' : pct >= 60 ? '#d97706' : pct > 0 ? '#2563eb' : '#64748b';

  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 12, padding: '16px 18px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', cursor: 'pointer',
      border: '2px solid transparent', transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{s.employee_name}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{s.employee_code} · {s.department_name || '—'}</div>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{pct}%</div>
      </div>

      {/* Progress bar */}
      <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6, marginBottom: 10, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>

      <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
        <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {s.done_tasks} xong</span>
        <span style={{ color: '#64748b' }}>○ {s.pending_tasks} chờ</span>
        {s.skipped_tasks > 0 && <span style={{ color: '#9ca3af' }}>– {s.skipped_tasks} bỏ qua</span>}
        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>{s.total_tasks} nhiệm vụ</span>
      </div>
    </div>
  );
}

/* ══════════════════ DETAIL ══════════════════ */
function DetailTab({ date, dayLogs, loading, employees, selectedEmpId, isManagerView, onSelectEmployee, onUpdateLog }) {
  const [noteModal, setNoteModal] = useState(null); // { logId, note }
  const [noteText, setNoteText] = useState('');

  const grouped = {};
  dayLogs.forEach(l => {
    const key = l.employee_id;
    if (!grouped[key]) grouped[key] = { name: l.employee_name, code: l.assigned_to_code || l.employee_code, dept: l.department_name, tasks: [] };
    grouped[key].tasks.push(l);
  });

  const done = dayLogs.filter(l => l.status === 'done').length;
  const total = dayLogs.length;

  return (
    <div>
      {isManagerView && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedEmpId || ''} onChange={e => onSelectEmployee(e.target.value || null)}
            style={{ padding: '8px 12px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 200 }}>
            <option value="">Tất cả nhân viên</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.employee_code}</option>)}
          </select>
          {total > 0 && (
            <span style={{ fontSize: 13, color: '#64748b' }}>
              <span style={{ fontWeight: 700, color: '#16a34a' }}>{done}</span>/{total} nhiệm vụ hoàn thành
              <span style={{ marginLeft: 8, color: '#6366f1', fontWeight: 700 }}>({Math.round(done/total*100)}%)</span>
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Đang tải...</div>
      ) : dayLogs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 12 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div>Nhân viên này chưa có nhiệm vụ nào được định nghĩa</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(grouped).map(([empId, group]) => (
            <EmployeeDayCard key={empId} group={group} date={date}
              onUpdateLog={onUpdateLog}
              onNoteClick={(logId, note) => { setNoteModal(logId); setNoteText(note || ''); }} />
          ))}
        </div>
      )}

      {/* Note modal */}
      {noteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setNoteModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Ghi chú nhiệm vụ</div>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
              style={{ width: '100%', minHeight: 90, padding: '8px 12px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Nhập ghi chú..." />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setNoteModal(null)} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Huỷ</button>
              <button onClick={() => {
                const log = dayLogs.find(l => l.log_id === noteModal);
                if (log) onUpdateLog(noteModal, log.status, noteText);
                setNoteModal(null);
              }} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Lưu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmployeeDayCard({ group, date, onUpdateLog, onNoteClick }) {
  const done = group.tasks.filter(t => t.status === 'done').length;
  const total = group.tasks.length;
  const pct = total > 0 ? Math.round(done / total * 100) : 0;
  const pctColor = pct === 100 ? '#16a34a' : pct >= 50 ? '#d97706' : '#64748b';

  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
      {/* Employee header */}
      <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0,
        }}>{group.name?.charAt(0)}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{group.name}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{group.code} · {group.dept || '—'}</div>
        </div>
        {/* Progress */}
        <div style={{ textAlign: 'right', minWidth: 80 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: pctColor }}>{pct}%</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{done}/{total}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: '#f1f5f9' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pctColor, transition: 'width 0.4s' }} />
      </div>

      {/* Task list */}
      <div style={{ padding: '8px 0' }}>
        {group.tasks.map(t => <TaskRow key={t.log_id || t.task_id} t={t} onUpdate={onUpdateLog} onNote={onNoteClick} />)}
      </div>
    </div>
  );
}

function TaskRow({ t, onUpdate, onNote }) {
  const catCfg = CATEGORY[t.category] || CATEGORY.other;
  const statusCfg = STATUS_CFG[t.status] || STATUS_CFG.pending;
  const isDone = t.status === 'done';
  const isSkipped = t.status === 'skipped';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px',
      borderBottom: '1px solid #f8fafc', transition: 'background 0.1s',
      background: isDone ? '#f0fdf4' : isSkipped ? '#fafafa' : '#fff',
    }}
      onMouseEnter={e => { if (!isDone) e.currentTarget.style.background = '#fafcff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = isDone ? '#f0fdf4' : isSkipped ? '#fafafa' : '#fff'; }}
    >
      {/* Checkbox */}
      <button onClick={() => onUpdate(t.log_id, isDone ? 'pending' : 'done', t.note)}
        style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0, cursor: 'pointer',
          border: isDone ? 'none' : '2px solid #d1d5db',
          background: isDone ? '#16a34a' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, color: '#fff', transition: 'all 0.15s',
        }}>
        {isDone && '✓'}
      </button>

      {/* Category icon */}
      <span style={{ fontSize: 15, flexShrink: 0 }}>{catCfg.icon}</span>

      {/* Title */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500,
          color: isDone ? '#86efac' : isSkipped ? '#94a3b8' : '#1e293b',
          textDecoration: isSkipped ? 'line-through' : 'none',
        }}>{t.title}</div>
        {t.description && !isDone && (
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</div>
        )}
        {t.note && (
          <div style={{ fontSize: 11, color: '#6366f1', marginTop: 1 }}>💬 {t.note}</div>
        )}
      </div>

      {/* Category badge */}
      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: catCfg.color, background: catCfg.bg, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {catCfg.label}
      </span>

      {/* Status + actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {!isDone && (
          <button onClick={() => onUpdate(t.log_id, isSkipped ? 'pending' : 'skipped', t.note)}
            style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#9ca3af' }}>
            {isSkipped ? '↩' : 'Bỏ qua'}
          </button>
        )}
        <button onClick={() => onNote(t.log_id, t.note)}
          style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#6366f1' }}>
          {t.note ? '💬' : '📝'}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ MANAGE TAB ══════════════════ */
function ManageTab({ definitions, employees, filterEmpDef, setFilterEmpDef, onRefresh }) {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ employee_id: '', title: '', description: '', category: 'work', sort_order: 0 });
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const inputStyle = { width: '100%', padding: '7px 11px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' };
  const lbl = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3, display: 'block' };

  const openCreate = () => {
    setForm({ employee_id: filterEmpDef || '', title: '', description: '', category: 'work', sort_order: 0 });
    setEditing(null); setFormError(''); setModal(true);
  };
  const openEdit = (d) => {
    setForm({ employee_id: d.employee_id, title: d.title, description: d.description || '', category: d.category, sort_order: d.sort_order, is_active: d.is_active });
    setEditing(d); setFormError(''); setModal(true);
  };

  const handleSubmit = async () => {
    if (!form.employee_id) { setFormError('Phải chọn nhân viên'); return; }
    if (!form.title.trim()) { setFormError('Tên nhiệm vụ không được để trống'); return; }
    setSubmitting(true); setFormError('');
    try {
      if (editing) await dailyTaskAPI.updateDefinition(editing.id, form);
      else await dailyTaskAPI.createDefinition(form);
      setModal(null); onRefresh();
    } catch (e) { setFormError(e.response?.data?.message || 'Lỗi lưu'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Xoá nhiệm vụ này?')) return;
    try { await dailyTaskAPI.deleteDefinition(id); onRefresh(); }
    catch (e) { alert(e.response?.data?.message || 'Không thể xoá'); }
  };

  // Group by employee
  const grouped = {};
  definitions.forEach(d => {
    if (!grouped[d.employee_id]) grouped[d.employee_id] = { name: d.employee_name, code: d.employee_code, dept: d.department_name, tasks: [] };
    grouped[d.employee_id].tasks.push(d);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={filterEmpDef} onChange={e => setFilterEmpDef(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 200 }}>
          <option value="">Tất cả nhân viên</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <button onClick={openCreate} style={{
          marginLeft: 'auto', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff',
          border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
        }}>+ Thêm nhiệm vụ</button>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 12, color: '#94a3b8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div>Chưa có nhiệm vụ nào. Nhấn "+ Thêm nhiệm vụ" để bắt đầu.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(grouped).map(([empId, g]) => (
            <div key={empId} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ padding: '12px 18px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>{g.name?.charAt(0)}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{g.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{g.code} · {g.dept || '—'}</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>{g.tasks.length} nhiệm vụ</span>
              </div>
              <div>
                {g.tasks.map((d, i) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', borderBottom: i < g.tasks.length - 1 ? '1px solid #f8fafc' : 'none', background: d.is_active ? '#fff' : '#fafafa' }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{CATEGORY[d.category]?.icon || '📌'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: d.is_active ? '#1e293b' : '#94a3b8', textDecoration: d.is_active ? 'none' : 'line-through' }}>{d.title}</div>
                      {d.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{d.description}</div>}
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: CATEGORY[d.category]?.color, background: CATEGORY[d.category]?.bg, flexShrink: 0 }}>{CATEGORY[d.category]?.label}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>#{d.sort_order}</span>
                    {!d.is_active && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#f3f4f6', color: '#9ca3af' }}>Tắt</span>}
                    <button onClick={() => openEdit(d)} style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#475569', flexShrink: 0 }}>Sửa</button>
                    <button onClick={() => handleDelete(d.id)} style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#dc2626', flexShrink: 0 }}>Xoá</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{editing ? 'Sửa nhiệm vụ' : 'Thêm nhiệm vụ hàng ngày'}</div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
            </div>
            <div style={{ padding: '18px 22px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!editing && (
                <div>
                  <label style={lbl}>Nhân viên *</label>
                  <select style={inputStyle} value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}>
                    <option value="">— Chọn nhân viên —</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={lbl}>Tên nhiệm vụ *</label>
                <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="VD: Kiểm tra email buổi sáng" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                <div>
                  <label style={lbl}>Danh mục</label>
                  <select style={inputStyle} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Thứ tự</label>
                  <input type="number" style={inputStyle} value={form.sort_order} min={0} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={lbl}>Mô tả</label>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Hướng dẫn thực hiện nhiệm vụ..." />
              </div>
              {editing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" id="isActive" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} />
                  <label htmlFor="isActive" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>Nhiệm vụ đang hoạt động</label>
                </div>
              )}
              {formError && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '7px 12px', borderRadius: 6, fontSize: 12 }}>{formError}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={() => setModal(null)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Huỷ</button>
                <button onClick={handleSubmit} disabled={submitting} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: submitting ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13 }}>
                  {submitting ? 'Đang lưu...' : 'Lưu'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
